import { CSSProperties, FC, ReactNode } from 'react';
import {
    TbBuilding,
    TbCalendarSearch,
    TbChartInfographic,
    TbChartLine,
    TbFileText,
    TbInputX,
    TbLayoutGrid,
    TbNews,
    TbSearch,
    TbUserSearch,
    TbUserX,
    TbWorld,
    TbWorldX,
} from 'react-icons/tb';

import EmptySection from '@/shared/components/layouts/views/EmptySection';

import type { AllSearchDomain } from '../../routes/00-All/useAllSearch';

export type SearchEmptyStateScope = AllSearchDomain | 'all' | 'companies';
export type SearchEmptyStateMode = 'initial' | 'no-results';

interface EmptyStateContent {
    description: string;
    icon: ReactNode;
    label: string;
}

interface SearchEmptyStateDefinition {
    initial: EmptyStateContent;
    'no-results': EmptyStateContent;
}

interface SearchEmptyStateProps {
    description?: ReactNode;
    height?: CSSProperties['height'];
    mode: SearchEmptyStateMode;
    scope: SearchEmptyStateScope;
}

const SEARCH_EMPTY_STATE_DEFINITIONS: Record<SearchEmptyStateScope, SearchEmptyStateDefinition> = {
    all: {
        initial: {
            description:
                'Search across companies, workspace resources, people, documents, economic data, news, and events.',
            icon: <TbSearch />,
            label: 'Search Across Your Entire Organization',
        },
        'no-results': {
            description: 'Try a broader query or remove filters to search across more sources.',
            icon: <TbInputX />,
            label: 'No Cross-Source Results Found',
        },
    },
    companies: {
        initial: {
            description: 'Search by company, fund, or ticker to discover public and workspace data.',
            icon: <TbBuilding />,
            label: 'Search Companies & Entities',
        },
        'no-results': {
            description: 'Try another company name or ticker, or remove some company filters.',
            icon: <TbInputX />,
            label: 'No Companies or Entities Found',
        },
    },
    documents: {
        initial: {
            description:
                'Search connected cloud storage alongside public filings and other indexed research documents.',
            icon: <TbFileText />,
            label: 'Search Documents & Filings',
        },
        'no-results': {
            description: 'Try a filename, issuer, filing type, keyword, or another connected source.',
            icon: <TbFileText />,
            label: 'No Documents or Filings Found',
        },
    },
    economic: {
        initial: {
            description:
                'Explore macroeconomic indicators, Treasury rates, risk premiums, commodities, foreign exchange, and crypto.',
            icon: <TbChartInfographic />,
            label: 'Explore Economic & Market Data',
        },
        'no-results': {
            description: 'Try an indicator, market, country, category, or broader source and frequency filters.',
            icon: <TbChartLine />,
            label: 'No Economic Series Found',
        },
    },
    events: {
        initial: {
            description: 'Explore dividend dates and macroeconomic releases across the market calendar.',
            icon: <TbCalendarSearch />,
            label: 'Explore Market Events',
        },
        'no-results': {
            description: 'Try another date range or remove event-source and impact filters.',
            icon: <TbCalendarSearch />,
            label: 'No Market Events Found',
        },
    },
    news: {
        initial: {
            description:
                'Search market news, press releases, fundraising, insider transactions, IPOs, and M&A activity.',
            icon: <TbNews />,
            label: 'Search News & Transactions',
        },
        'no-results': {
            description: 'Try broader terms or select another news or transaction feed.',
            icon: <TbNews />,
            label: 'No News or Transactions Found',
        },
    },
    people: {
        initial: {
            description:
                'Find people from connected email and calendar sources, then refine by identity, role, education, or keywords.',
            icon: <TbUserSearch />,
            label: 'Search People & Relationships',
        },
        'no-results': {
            description: 'Try a name, email address, company, title, or broader relationship filters.',
            icon: <TbUserX />,
            label: 'No People Found',
        },
    },
    trade: {
        initial: {
            description:
                'Explore trade corridors, hubs, regions, transport modes, and connected global-trade datasets.',
            icon: <TbWorld />,
            label: 'Explore Global Trade',
        },
        'no-results': {
            description: 'Try another region, corridor, transport mode, or connected trade source.',
            icon: <TbWorldX />,
            label: 'No Global Trade Data Found',
        },
    },
    workspace: {
        initial: {
            description:
                'Find investments, memos, pipelines, folders, lists, dashboards, tasks, and other resources available to you.',
            icon: <TbLayoutGrid />,
            label: 'Search Workspace Resources',
        },
        'no-results': {
            description: 'Try another resource name or remove resource-type, ownership, and status filters.',
            icon: <TbLayoutGrid />,
            label: 'No Workspace Resources Found',
        },
    },
};

const SearchEmptyState: FC<SearchEmptyStateProps> = ({ description, height, mode, scope }): JSX.Element => {
    const content = SEARCH_EMPTY_STATE_DEFINITIONS[scope][mode];

    return (
        <EmptySection
            description={description ?? content.description}
            height={height}
            icon={content.icon}
            label={content.label}
        />
    );
};

export default SearchEmptyState;
